import classNames from "classnames";
import moment from "moment";
import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { PageHeader } from '../components/ui/bootstrap';
import PodcastEpisodeSettings from "../components/PodcastEpisodeSettings";
import { usePodcast, useAvailableContent } from "../http-hooks/podcast/usePodcast";
import { Plus } from 'lucide-react';

import Spinner from '../components/Spinner';
import TextRowListSkeleton from '../components/skeletons/pages/TextRowListSkeleton';

import leftArrowIcon from '../assets/arrow-left.svg';
import formatAudioFileDuration from "../utils/AudioDuration";

function AddContentToPodcast() {

    const { org_id, id, podcast_id } = useParams();
    const history = useHistory();

    // Use React Query hooks
    const { data: podcast } = usePodcast({ org_id, integration_id: id, podcast_id });
    const { 
        data: contentData, 
        isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useAvailableContent({ org_id, integration_id: id, podcast_id });
    
    // Flatten paginated content data
    const content = contentData?.pages?.flatMap(page => page.data) ?? [];

    const [show, setShow] = useState(false);
    const [selectedEpisode, setSelectedEpisode] = useState(null)

    const addItemToPodcast = (podcastItem) => {
        setSelectedEpisode(podcastItem);
        setShow(true);
    }

    const Content = () => {

        return (
            <React.Fragment>
                <div className="list-group list-group-flush">
                    {
                        content.map((item, index) => {
                            return (
                                <li 
                                    className={classNames(
                                        "list-group-item",
                                        "border",
                                        "rounded-3",
                                        "mb-3",
                                        "p-3",
                                        "transition-all",
                                        {
                                            "opacity-50": item.is_present
                                        }
                                    )}
                                    style={{
                                        transition: 'all 0.2s ease',
                                        cursor: 'default'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!item.is_present) {
                                            e.currentTarget.style.backgroundColor = 'var(--bs-light-bg-subtle)';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '';
                                        e.currentTarget.style.boxShadow = '';
                                        e.currentTarget.style.transform = '';
                                    }}
                                    key={`content-${index}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center gap-3">
                                        <div className="flex-fill overflow-hidden">
                                            <h4 className="mb-2 text-truncate fs-5 fw-bold text-dark">
                                                {item.title}
                                            </h4>
                                            <div className="d-flex align-items-center flex-wrap gap-2 small text-muted">
                                                {
                                                    (null !== item.published_at)
                                                    && (
                                                        <span className="fw-medium">
                                                            Published on {moment(item.published_at).format("MMM D, YYYY")}
                                                        </span>
                                                    )
                                                }
                                                {
                                                    item.audio_duration && (
                                                        <>
                                                            {item.published_at && <span>•</span>}
                                                            <span className="fw-medium">
                                                                {formatAudioFileDuration(item.audio_duration)}
                                                            </span>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        {!item.is_present && (
                                            <button 
                                                onClick={() => addItemToPodcast(item)}
                                                className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-3 fw-semibold shadow-sm flex-shrink-0"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(13, 110, 253, 0.25)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = '';
                                                    e.currentTarget.style.boxShadow = '';
                                                }}
                                                style={{ transition: 'all 0.2s ease' }}
                                            >
                                                <Plus size={18} />
                                                <span>Add</span>
                                            </button>
                                        )}
                                    </div>
                                </li>
                            )
                        })
                    }
                </div>
                {
                    isFetchingNextPage ? (
                        <button className={classNames("btn--fetch-more", "mt-3")}>
                            <Spinner color="var(--bs-secondary-color)" />
                        </button>
                    ) : hasNextPage ? (
                        <button className={classNames("btn--fetch-more", "mt-3")} onClick={() => fetchNextPage()}>
                            Fetch More
                        </button>
                    ) : null
                }

            </React.Fragment>
        )

    }

    return <>
        {
            isLoading
                ?
                (
                    <TextRowListSkeleton />
                )
                :
                (
                    <>
                        {
                            selectedEpisode && (
                                <PodcastEpisodeSettings
                                    show={show}
                                    onHide={() => setShow(false)}
                                    orgId={org_id}
                                    integrationId={id}
                                    podcastId={podcast_id}
                                    isNew={true}
                                    selectedEpisode={{
                                        title: selectedEpisode.title,
                                        content_id: selectedEpisode.SK.split('#')[1],
                                        description: "",
                                        author: podcast.author,
                                        category: podcast.category,
                                        is_published: true,
                                    }}
                                />
                            )
                        }
                        <PageHeader
                            title={`Add episode to ${podcast.title}`}
                            subtitle="Select an audio from below to add as episode. If the audio does not exist, add it from the Audios section."
                            left={
                                <div
                                    className="d-flex align-items-center gap-1"
                                    style={{ color: 'var(--blue)', cursor: 'pointer', fontSize: 13 }}
                                    onClick={() => history.push(`/organisation/${org_id}/integration/${id}/podcast/${podcast_id}/episodes`)}
                                >
                                    <img src={leftArrowIcon} width={16} alt="Back" />
                                    Back
                                </div>
                            }
                        />
                        <Content />
                    </>
                )
        }
    </>
}

export default AddContentToPodcast;
